import React, { useState, useRef, useEffect, useCallback } from 'react';
import './LeakPlayer.css';

const TRACKS = [
  { src: '/leaksongs/1.wav', label: '00_01' },
  { src: '/leaksongs/2.wav', label: '00_02' },
];

const LeakPlayer: React.FC = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [trackIdx, setTrackIdx] = useState(0);
  const shouldPlayRef = useRef(false);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'none';
    audioRef.current = audio;

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      shouldPlayRef.current = true;
      setTrackIdx(prev => (prev + 1) % TRACKS.length);
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.pause();
      audio.src = '';
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.src = TRACKS[trackIdx].src;
    if (shouldPlayRef.current) {
      audio.play().catch(() => {});
      shouldPlayRef.current = false;
    }
  }, [trackIdx]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play().catch(() => {});
    }
  }, [playing]);

  const skip = useCallback((dir: 1 | -1) => {
    shouldPlayRef.current = true;
    setTrackIdx(prev => (prev + dir + TRACKS.length) % TRACKS.length);
  }, []);

  return (
    <div className={`leak-player ${playing ? 'playing' : ''}`}>
      <button className="leak-skip" onClick={() => skip(-1)} aria-label="Previous track">
        ‹
      </button>
      <button className="leak-main" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
        <span className="eq-bars">
          <span className="eq-bar" />
          <span className="eq-bar" />
          <span className="eq-bar" />
        </span>
        <span className="leak-label">{TRACKS[trackIdx].label}</span>
      </button>
      <button className="leak-skip" onClick={() => skip(1)} aria-label="Next track">
        ›
      </button>
    </div>
  );
};

export default LeakPlayer;
